import { useState, useEffect, useRef } from 'react';
import { Mic, Play, Save, Loader2 } from 'lucide-react';
import { ope, API_BASE } from '../../../services/api';
import { useToast } from '../../../context/ToastContext';

const DEFAULT_AUDIO_VOICE_OPTIONS = [
    { value: 'es-ES-AlvaroNeural', label: 'Alvaro (Masculina)' },
    { value: 'es-ES-ElviraNeural', label: 'Elvira (Femenina)' },
    { value: 'es-ES-XimenaNeural', label: 'Ximena (Femenina)' }
];
const GENERATE_AUDIO_CLIENT_TIMEOUT_MS = 45000;
const AUDIO_COMPLETION_POLL_INTERVAL_MS = 30000;
const AUDIO_COMPLETION_POLL_MAX_ATTEMPTS = 140;

const OpeAdminAudioController = ({ temaId, numAudios, onAudioGenerated }) => {
    const [generatingAudio, setGeneratingAudio] = useState(false);
    const [savingAudioConfig, setSavingAudioConfig] = useState(false);
    const [testingAudio, setTestingAudio] = useState(false);
    const [audioConfig, setAudioConfig] = useState({
        voice: 'es-ES-AlvaroNeural',
        rate: '+0%',
        pitch: '+0Hz',
        volume: '+0%'
    });
    const [audioVoiceOptions, setAudioVoiceOptions] = useState(DEFAULT_AUDIO_VOICE_OPTIONS);
    const [testPhrase, setTestPhrase] = useState('Hola. Esta es una prueba de voz para la OPE de TUVISA.');
    const [previewAudioUrl, setPreviewAudioUrl] = useState('');
    const [previewAudioMeta, setPreviewAudioMeta] = useState(null);
    const previewAudioRef = useRef(null);
    const { success, error, info } = useToast();

    useEffect(() => {
        if (!temaId) return;
        loadAudioConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [temaId]);

    useEffect(() => {
        return () => {
            if (previewAudioUrl && previewAudioUrl.startsWith('blob:')) {
                URL.revokeObjectURL(previewAudioUrl);
            }
        };
    }, [previewAudioUrl]);

    const loadAudioConfig = async () => {
        try {
            const data = await ope.adminGetAudioConfig(temaId);
            if (data?.config) setAudioConfig(data.config);
            if (Array.isArray(data?.voices) && data.voices.length > 0) {
                setAudioVoiceOptions(data.voices);
            }
            if (data?.warning) info(data.warning);
        } catch (err) {
            error(err.message || 'Error al cargar configuracion de audio');
        }
    };

    const saveAudioConfig = async () => {
        const data = await ope.adminSaveAudioConfig(temaId, audioConfig);
        if (data?.config) setAudioConfig(data.config);
        if (Array.isArray(data?.voices) && data.voices.length > 0) {
            setAudioVoiceOptions(data.voices);
        }
        if (data?.warning) info(data.warning);
        return data?.config || audioConfig;
    };

    const fetchProtectedAudioAsBlobUrl = async (audioUrl) => {
        const token = localStorage.getItem('token');
        const normalizedAudioUrl = (() => {
            if (!audioUrl) return '';
            if (/^https?:\/\//i.test(audioUrl)) return audioUrl;
            return `${API_BASE.replace('/api', '')}${audioUrl}`;
        })();

        const response = await fetch(normalizedAudioUrl, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: 'include'
        });
        if (!response.ok) throw new Error('No se pudo descargar el audio de prueba');

        const contentType = String(response.headers.get('content-type') || '').toLowerCase();
        if (!contentType.includes('audio/')) {
            const payload = await response.text();
            throw new Error(payload ? `Respuesta no-audio: ${payload.slice(0, 120)}` : 'La respuesta no contiene audio');
        }

        const rawBlob = await response.blob();
        const blob = rawBlob.type && rawBlob.type.startsWith('audio/')
            ? rawBlob
            : new Blob([rawBlob], { type: 'audio/mpeg' });
        const url = URL.createObjectURL(blob);

        setPreviewAudioUrl(prev => {
            if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
            return url;
        });

        return url;
    };

    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const getLatestTemaAudioFilename = async (targetTemaId) => {
        try {
            const secciones = await ope.getSecciones(targetTemaId);
            if (!Array.isArray(secciones) || secciones.length === 0) return null;
            const latest = [...secciones].sort((a, b) => (b.id || 0) - (a.id || 0))[0];
            return latest?.audio_filename || null;
        } catch {
            return null;
        }
    };

    const pollAudioGenerationCompletion = async (targetTemaId, previousFilename) => {
        for (let attempt = 0; attempt < AUDIO_COMPLETION_POLL_MAX_ATTEMPTS; attempt += 1) {
            await sleep(AUDIO_COMPLETION_POLL_INTERVAL_MS);
            const latestFilename = await getLatestTemaAudioFilename(targetTemaId);
            if (latestFilename && (!previousFilename || latestFilename !== previousFilename)) {
                if (onAudioGenerated) onAudioGenerated();
                success('Audio generado correctamente');
                return true;
            }
        }
        info('La generacion de audio sigue en curso. Recarga esta pantalla en unos minutos.');
        return false;
    };

    const handleSaveAudioConfig = async () => {
        try {
            setSavingAudioConfig(true);
            await saveAudioConfig();
            success('Configuracion de audio guardada');
        } catch (err) {
            error(err.message || 'Error al guardar configuracion de audio');
        } finally {
            setSavingAudioConfig(false);
        }
    };

    const handleTestAudio = async () => {
        try {
            setTestingAudio(true);
            const result = await ope.adminTestAudio(temaId, {
                ...audioConfig,
                text: testPhrase
            });

            if (result?.config) setAudioConfig(result.config);
            if (Array.isArray(result?.voices) && result.voices.length > 0) {
                setAudioVoiceOptions(result.voices);
            }
            if (result?.warning) info(result.warning);
            setPreviewAudioMeta(result);

            const blobUrl = await fetchProtectedAudioAsBlobUrl(result.audioUrl);
            if (previewAudioRef.current) {
                previewAudioRef.current.src = blobUrl;
                previewAudioRef.current.load();
                previewAudioRef.current.play().catch((playError) => {
                    info(`Audio listo. Pulsa Play manualmente (${playError?.name || 'autoplay bloqueado'})`);
                });
            }

            success('Prueba de audio generada');
        } catch (err) {
            error(err.message || 'Error al generar audio de prueba');
        } finally {
            setTestingAudio(false);
        }
    };

    const handleGenerateAudio = async () => {
        if (!temaId) return;
        let requestTimeoutId = null;
        let previousFilename = null;
        try {
            setGeneratingAudio(true);
            previousFilename = await getLatestTemaAudioFilename(temaId);
            const config = await saveAudioConfig();
            const controller = new AbortController();
            requestTimeoutId = setTimeout(() => controller.abort(), GENERATE_AUDIO_CLIENT_TIMEOUT_MS);

            const result = await ope.adminGenerateAudio(temaId, config, { signal: controller.signal });
            if (result?.config) setAudioConfig(result.config);
            if (Array.isArray(result?.voices) && result.voices.length > 0) {
                setAudioVoiceOptions(result.voices);
            }
            if (result?.warning) info(result.warning);

            if (onAudioGenerated) onAudioGenerated();
            success('Audio generado correctamente');
        } catch (err) {
            const message = String(err?.message || '');
            const aborted = err?.name === 'AbortError' || /aborted|aborterror/i.test(message);
            if (aborted) {
                info('Generacion iniciada. Puede tardar bastante; te avisare cuando termine.');
                pollAudioGenerationCompletion(temaId, previousFilename)
                    .catch(() => info('No se pudo comprobar el estado del audio. Recarga en unos minutos.'));
                return;
            }
            console.error('Error generating audio:', err);
            error(err.message || 'Error al generar audio');
        } finally {
            if (requestTimeoutId) clearTimeout(requestTimeoutId);
            setGeneratingAudio(false);
        }
    };

    return (
        <div className="p-4 bg-white/5 rounded-xl border border-white/10 space-y-4 ml-4">
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs text-gray-400 mb-1">Voz</label>
                    <select
                        value={audioConfig.voice}
                        onChange={e => setAudioConfig(prev => ({ ...prev, voice: e.target.value }))}
                        className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                    >
                        {audioVoiceOptions.map(voice => (
                            <option key={voice.value} value={voice.value}>{voice.label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs text-gray-400 mb-1">Velocidad</label>
                    <select
                        value={audioConfig.rate}
                        onChange={e => setAudioConfig(prev => ({ ...prev, rate: e.target.value }))}
                        className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                    >
                        <option value="-20%">Lento (-20%)</option>
                        <option value="-10%">Algo lento (-10%)</option>
                        <option value="+0%">Normal</option>
                        <option value="+10%">Algo rapido (+10%)</option>
                        <option value="+20%">Rapido (+20%)</option>
                    </select>
                </div>
                <div>
                    <label className="block text-xs text-gray-400 mb-1">Tono</label>
                    <select
                        value={audioConfig.pitch}
                        onChange={e => setAudioConfig(prev => ({ ...prev, pitch: e.target.value }))}
                        className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                    >
                        <option value="-10Hz">Grave (-10Hz)</option>
                        <option value="+0Hz">Normal</option>
                        <option value="+10Hz">Agudo (+10Hz)</option>
                    </select>
                </div>
                <div>
                    <label className="block text-xs text-gray-400 mb-1">Volumen</label>
                    <select
                        value={audioConfig.volume}
                        onChange={e => setAudioConfig(prev => ({ ...prev, volume: e.target.value }))}
                        className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                    >
                        <option value="-10%">Bajo (-10%)</option>
                        <option value="+0%">Normal</option>
                        <option value="+10%">Alto (+10%)</option>
                    </select>
                </div>
            </div>

            <div>
                <label className="block text-xs text-gray-400 mb-1">Frase de prueba</label>
                <textarea
                    value={testPhrase}
                    onChange={e => setTestPhrase(e.target.value)}
                    rows={3}
                    maxLength={400}
                    className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500 resize-y"
                    placeholder="Escribe una frase para escuchar como suena esta configuracion"
                />
                <p className="text-xs text-gray-500 mt-1">{testPhrase.length}/400 caracteres</p>
            </div>

            <div className="flex flex-wrap gap-2">
                <button
                    onClick={handleSaveAudioConfig}
                    disabled={savingAudioConfig || testingAudio || generatingAudio}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-600 hover:bg-slate-700 disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
                >
                    {savingAudioConfig ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</>
                    ) : (
                        <><Save className="w-4 h-4" /> Guardar</>
                    )}
                </button>

                <button
                    onClick={handleTestAudio}
                    disabled={testingAudio || !testPhrase.trim()}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
                >
                    {testingAudio ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Generando...</>
                    ) : (
                        <><Play className="w-4 h-4" /> Probar Voz</>
                    )}
                </button>

                <button
                    onClick={handleGenerateAudio}
                    disabled={generatingAudio}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
                >
                    {generatingAudio ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Generando audio... (tarda aprox 3min)</>
                    ) : (
                        <><Mic className="w-4 h-4" /> {numAudios > 0 ? 'Regenerar Audio' : 'Generar Audio'}</>
                    )}
                </button>
            </div>

            {previewAudioMeta && (
                <div className="text-xs text-cyan-300 bg-cyan-900/20 border border-cyan-500/30 rounded-lg px-3 py-2">
                    Prueba generada: {previewAudioMeta.duracion}s aprox. ({previewAudioMeta.textChars} chars)
                </div>
            )}

            {previewAudioUrl && (
                <audio ref={previewAudioRef} controls src={previewAudioUrl} className="w-full" />
            )}
        </div>
    );
};

export default OpeAdminAudioController;
